import { useRef, useState } from 'react';
import { APP_FONTS, APP_LANGUAGES, APP_THEMES, APP_VISUAL_STYLES } from '../constants';
import type { AppFont, AppLanguage, AppSettings, AppTheme, AppVisualStyle, NotificationStyle } from '../types';
import type { BillingDiagnostics } from '../lib/monetization';
import type { MonetizationPlan } from '../lib/monetization';

type OptionsTabProps = {
  settings: AppSettings;
  activeThemeDescription: string;
  activeVisualStyleDescription: string;
  activeFontDescription: string;
  isWebApp: boolean;
  labels: {
    theme: string;
    styleLanguage: string;
    visualTheme: string;
    typography: string;
    appFont: string;
    language: string;
    appearance: string;
    compact: string;
    simplifiedMode: string;
    tips: string;
    rescueDefaults: string;
    soundDefault: string;
    notifications: string;
    notificationsDesc: string;
    notificationStyle: string;
    notificationStyleOff: string;
    notificationStyleQuiet: string;
    notificationStyleStandard: string;
    notificationExactAlarm: string;
    notificationExactAlarmGranted: string;
    notificationExactAlarmDenied: string;
    notificationExactAlarmUnknown: string;
    notificationExactAlarmOpenSettings: string;
    notificationAndroidBatteryNote: string;
    notificationAndroidBatteryWarning: string;
    reentryNudges: string;
    dailyReflectionPrompt: string;
    weeklyPatternPrompt: string;
    safety: string;
    confirmDelete: string;
    monetization: string;
    adsEnabled: string;
    personalizedAds: string;
    personalizedAdsStatus: string;
    personalizedAdsStatusUnknown: string;
    personalizedAdsStatusAccepted: string;
    personalizedAdsStatusRejected: string;
    personalizedAdsStatusNotRequired: string;
    withdrawAdConsent: string;
    adPrivacyOptions: string;
    adsCmpNotice: string;
    billingDescription: string;
    billingConfigured: string;
    billingNotConfigured: string;
    billingStatus: string;
    billingTier: string;
    billingTierFree: string;
    billingTierAdFree: string;
    billingTierPro: string;
    billingProBenefitsTitle: string;
    billingProBenefitsBody: string;
    billingCheck: string;
    billingRestore: string;
    billingDisclosure: string;
    billingPlans: string;
    billingNoPlans: string;
    billingBuy: string;
    billingCurrent: string;
    billingManage: string;
    billingManageUnavailable: string;
    billingDiagnosticsTitle: string;
    billingDiagnosticsRun: string;
    billingDiagnosticsClearEvents: string;
    billingDiagnosticsDone: string;
    billingDiagnosticsFailed: string;
    billingDiagnosticsNotAvailable: string;
    billingDiagnosticsLastCheck: string;
    billingDiagnosticsChecks: string;
    billingDiagnosticsCheckOk: string;
    billingDiagnosticsCheckNeedsAction: string;
    billingDiagnosticsDetails: string;
    billingDiagnosticsAppUserId: string;
    billingDiagnosticsOffering: string;
    billingDiagnosticsPackages: string;
    billingDiagnosticsEntitlements: string;
    billingDiagnosticsSubscriptions: string;
    billingDiagnosticsManagementUrl: string;
    billingDiagnosticsRecentEvents: string;
    billingDiagnosticsNoEvents: string;
    billingAdFreeTag: string;
    billingProTag: string;
    backup: string;
    backupDescription: string;
    localFirstTitle: string;
    localFirstBody: string;
    localFirstRisk: string;
    backupPassphrase: string;
    backupCreate: string;
    backupRestore: string;
    backupStatusPrefix: string;
    syncInProgress: string;
    drive: string;
    driveDescription: string;
    driveConnect: string;
    driveDisconnect: string;
    driveUpload: string;
    driveRestore: string;
    driveConfigured: string;
    driveNotConfigured: string;
    driveConnected: string;
    driveNotConnected: string;
    help: string;
    replayTitle: string;
    replayDescription: string;
    replayButton: string;
    privacyData: string;
    privacyDescription: string;
    privacyPolicy: string;
    terms: string;
    privacyRightsSla: string;
    childrenNotice: string;
    medicalDisclaimer: string;
    contactSupport: string;
    exportData: string;
    deleteData: string;
  };
  onUpdateSetting: <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => void;
  exactAlarmStatus: 'granted' | 'denied' | 'unknown';
  onOpenExactAlarmSettings: () => Promise<void>;
  adConsentStatus: 'unknown' | 'accepted' | 'rejected' | 'not_required';
  adPrivacyOptionsAvailable: boolean;
  onUpdateAdPersonalization: (enabled: boolean) => Promise<void>;
  onWithdrawAdConsent: () => void;
  onOpenAdPrivacyOptions: () => Promise<void>;
  billingConfigured: boolean;
  billingTier: 'free' | 'ad_free' | 'pro';
  billingStatusText: string | null;
  onCheckBilling: () => Promise<void>;
  onRestoreBilling: () => Promise<void>;
  canManageBilling: boolean;
  onManageBilling: () => Promise<void>;
  billingDiagnostics: BillingDiagnostics | null;
  onClearBillingEvents: () => void;
  onRunBillingDiagnostics: () => Promise<void>;
  monetizationPlans: MonetizationPlan[];
  onBuyPlan: (productId: string) => Promise<void>;
  onCreateBackup: (passphrase: string) => Promise<void>;
  onRestoreBackup: (file: File, passphrase: string) => Promise<void>;
  onConnectDrive: () => Promise<void>;
  onDisconnectDrive: () => void;
  onUploadDriveBackup: (passphrase: string) => Promise<void>;
  onRestoreDriveBackup: (passphrase: string) => Promise<void>;
  driveConfigured: boolean;
  driveConnected: boolean;
  syncActivity: string | null;
  backupStatus: string | null;
  backupStatusTone: 'neutral' | 'success' | 'error';
  onReplayOnboarding: () => void;
  onOpenPrivacyPolicy: () => void;
  onOpenTerms: () => void;
  onContactSupport: () => Promise<void>;
  onExportData: () => Promise<void>;
  onDeleteAllData: () => Promise<void>;
};

export function OptionsTab({
  settings,
  activeThemeDescription,
  activeVisualStyleDescription,
  activeFontDescription,
  isWebApp,
  labels,
  onUpdateSetting,
  exactAlarmStatus,
  onOpenExactAlarmSettings,
  adConsentStatus,
  adPrivacyOptionsAvailable,
  onUpdateAdPersonalization,
  onWithdrawAdConsent,
  onOpenAdPrivacyOptions,
  billingConfigured,
  billingTier,
  billingStatusText,
  onCheckBilling,
  onRestoreBilling,
  canManageBilling,
  onManageBilling,
  billingDiagnostics,
  onClearBillingEvents,
  onRunBillingDiagnostics,
  monetizationPlans,
  onBuyPlan,
  onCreateBackup,
  onRestoreBackup,
  onConnectDrive,
  onDisconnectDrive,
  onUploadDriveBackup,
  onRestoreDriveBackup,
  driveConfigured,
  driveConnected,
  syncActivity,
  backupStatus,
  backupStatusTone,
  onReplayOnboarding,
  onOpenPrivacyPolicy,
  onOpenTerms,
  onContactSupport,
  onExportData,
  onDeleteAllData
}: OptionsTabProps) {
  const [backupPassphrase, setBackupPassphrase] = useState('');
  const [isWorking, setIsWorking] = useState(false);
  const restoreInputRef = useRef<HTMLInputElement | null>(null);

  async function handleCreateBackup(): Promise<void> {
    if (isWorking) return;
    setIsWorking(true);
    try {
      await onCreateBackup(backupPassphrase);
    } finally {
      setIsWorking(false);
    }
  }

  async function handleRestoreFile(file: File): Promise<void> {
    if (isWorking) return;
    setIsWorking(true);
    try {
      await onRestoreBackup(file, backupPassphrase);
    } finally {
      setIsWorking(false);
      if (restoreInputRef.current) restoreInputRef.current.value = '';
    }
  }

  async function handleDriveConnect(): Promise<void> {
    if (isWorking) return;
    setIsWorking(true);
    try {
      await onConnectDrive();
    } finally {
      setIsWorking(false);
    }
  }

  async function handleDriveUpload(): Promise<void> {
    if (isWorking) return;
    setIsWorking(true);
    try {
      await onUploadDriveBackup(backupPassphrase);
    } finally {
      setIsWorking(false);
    }
  }

  async function handleDriveRestore(): Promise<void> {
    if (isWorking) return;
    setIsWorking(true);
    try {
      await onRestoreDriveBackup(backupPassphrase);
    } finally {
      setIsWorking(false);
    }
  }

  async function handleCheckBilling(): Promise<void> {
    if (isWorking) return;
    setIsWorking(true);
    try {
      await onCheckBilling();
    } finally {
      setIsWorking(false);
    }
  }

  async function handleRestoreBilling(): Promise<void> {
    if (isWorking) return;
    setIsWorking(true);
    try {
      await onRestoreBilling();
    } finally {
      setIsWorking(false);
    }
  }

  async function handleBuyPlan(productId: string): Promise<void> {
    if (isWorking) return;
    setIsWorking(true);
    try {
      await onBuyPlan(productId);
    } finally {
      setIsWorking(false);
    }
  }

  async function handleManageBilling(): Promise<void> {
    if (isWorking) return;
    setIsWorking(true);
    try {
      await onManageBilling();
    } finally {
      setIsWorking(false);
    }
  }

  async function handleRunBillingDiagnostics(): Promise<void> {
    if (isWorking) return;
    setIsWorking(true);
    try {
      await onRunBillingDiagnostics();
    } finally {
      setIsWorking(false);
    }
  }

  async function handlePrivacyPolicy(): Promise<void> {
    if (isWorking) return;
    onOpenPrivacyPolicy();
  }

  async function handleTerms(): Promise<void> {
    if (isWorking) return;
    onOpenTerms();
  }

  async function handleContactSupport(): Promise<void> {
    if (isWorking) return;
    setIsWorking(true);
    try {
      await onContactSupport();
    } finally {
      setIsWorking(false);
    }
  }

  async function handleExportData(): Promise<void> {
    if (isWorking) return;
    setIsWorking(true);
    try {
      await onExportData();
    } finally {
      setIsWorking(false);
    }
  }

  async function handleDeleteAllData(): Promise<void> {
    if (isWorking) return;
    setIsWorking(true);
    try {
      await onDeleteAllData();
    } finally {
      setIsWorking(false);
    }
  }

  const tierLabel =
    billingTier === 'pro'
      ? labels.billingTierPro
      : billingTier === 'ad_free'
        ? labels.billingTierAdFree
        : labels.billingTierFree;
  const adsLocked = billingTier !== 'free';
  const reminderControlsDisabled = settings.notificationStyle === 'off';
  const showAndroidWarning = exactAlarmStatus !== 'granted';
  const adConsentStatusLabel =
    adConsentStatus === 'accepted'
      ? labels.personalizedAdsStatusAccepted
      : adConsentStatus === 'rejected'
        ? labels.personalizedAdsStatusRejected
        : adConsentStatus === 'not_required'
          ? labels.personalizedAdsStatusNotRequired
          : labels.personalizedAdsStatusUnknown;

  return (
    <section>
      <h2>{labels.theme}</h2>
      <div className="stack">
        <label className="setting-row">
          <span>{labels.styleLanguage}</span>
          <select
            className="setting-select"
            value={settings.visualStyle}
            onChange={(e) => onUpdateSetting('visualStyle', e.target.value as AppVisualStyle)}
          >
            {APP_VISUAL_STYLES.map((item) => (
              <option key={item.style} value={item.style}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <div className="small">{activeVisualStyleDescription}</div>
        <label className="setting-row">
          <span>{labels.visualTheme}</span>
          <select className="setting-select" value={settings.theme} onChange={(e) => onUpdateSetting('theme', e.target.value as AppTheme)}>
            {APP_THEMES.map((item) => (
              <option key={item.theme} value={item.theme}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <div className="small">{activeThemeDescription}</div>
      </div>

      <h2>{labels.typography}</h2>
      <div className="stack">
        <label className="setting-row">
          <span>{labels.appFont}</span>
          <select className="setting-select" value={settings.font} onChange={(e) => onUpdateSetting('font', e.target.value as AppFont)}>
            {APP_FONTS.map((item) => (
              <option key={item.font} value={item.font}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <div className="small">{activeFontDescription}</div>
        <label className="setting-row">
          <span>{labels.language}</span>
          <select
            className="setting-select"
            value={settings.language}
            onChange={(e) => onUpdateSetting('language', e.target.value as AppLanguage)}
          >
            {APP_LANGUAGES.map((item) => (
              <option key={item.language} value={item.language}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <h2>{labels.appearance}</h2>
      <div className="stack">
        <label className="setting-row">
          <span>{labels.compact}</span>
          <input type="checkbox" checked={settings.compactUi} onChange={(e) => onUpdateSetting('compactUi', e.target.checked)} />
        </label>
        <label className="setting-row">
          <span>{labels.simplifiedMode}</span>
          <input
            type="checkbox"
            checked={settings.simplifiedMode}
            onChange={(e) => onUpdateSetting('simplifiedMode', e.target.checked)}
          />
        </label>
        <label className="setting-row">
          <span>{labels.tips}</span>
          <input type="checkbox" checked={settings.showTips} onChange={(e) => onUpdateSetting('showTips', e.target.checked)} />
        </label>
      </div>

      <h2>{labels.rescueDefaults}</h2>
      <div className="stack">
        <label className="setting-row">
          <span>{labels.soundDefault}</span>
          <input
            type="checkbox"
            checked={settings.defaultRescueSound}
            onChange={(e) => onUpdateSetting('defaultRescueSound', e.target.checked)}
          />
        </label>
      </div>

      <h2>{labels.notifications}</h2>
      <div className="stack">
        <div className="small options-inline-note">{labels.notificationsDesc}</div>
        <label className="setting-row">
          <span>{labels.notificationStyle}</span>
          <select
            className="setting-select"
            value={settings.notificationStyle}
            onChange={(e) => onUpdateSetting('notificationStyle', e.target.value as NotificationStyle)}
          >
            <option value="off">{labels.notificationStyleOff}</option>
            <option value="quiet">{labels.notificationStyleQuiet}</option>
            <option value="standard">{labels.notificationStyleStandard}</option>
          </select>
        </label>
        <div className="small options-inline-note">
          {labels.notificationExactAlarm}:{' '}
          {exactAlarmStatus === 'granted'
            ? labels.notificationExactAlarmGranted
            : exactAlarmStatus === 'denied'
              ? labels.notificationExactAlarmDenied
              : labels.notificationExactAlarmUnknown}
        </div>
        {exactAlarmStatus === 'denied' && (
          <div className="settings-actions-inline options-single-action">
            <button className="settings-action" disabled={isWorking} onClick={() => void onOpenExactAlarmSettings()}>
              {labels.notificationExactAlarmOpenSettings}
            </button>
          </div>
        )}
        <div className={`small ${showAndroidWarning ? 'sync-status sync-status-error' : 'options-inline-note'}`}>
          {showAndroidWarning ? labels.notificationAndroidBatteryWarning : labels.notificationAndroidBatteryNote}
        </div>
        <label className="setting-row">
          <span>{labels.reentryNudges}</span>
          <input
            type="checkbox"
            checked={settings.reentryNudges}
            disabled={reminderControlsDisabled}
            onChange={(e) => onUpdateSetting('reentryNudges', e.target.checked)}
          />
        </label>
        <label className="setting-row">
          <span>{labels.dailyReflectionPrompt}</span>
          <input
            type="checkbox"
            checked={settings.dailyReflectionPrompt}
            disabled={reminderControlsDisabled}
            onChange={(e) => onUpdateSetting('dailyReflectionPrompt', e.target.checked)}
          />
        </label>
        <label className="setting-row">
          <span>{labels.weeklyPatternPrompt}</span>
          <input
            type="checkbox"
            checked={settings.weeklyPatternPrompt}
            disabled={reminderControlsDisabled}
            onChange={(e) => onUpdateSetting('weeklyPatternPrompt', e.target.checked)}
          />
        </label>
      </div>

      <h2>{labels.safety}</h2>
      <div className="stack">
        <label className="setting-row">
          <span>{labels.confirmDelete}</span>
          <input type="checkbox" checked={settings.confirmDelete} onChange={(e) => onUpdateSetting('confirmDelete', e.target.checked)} />
        </label>
      </div>

      <h2>{labels.monetization}</h2>
      <div className="stack">
        <label className="setting-row">
          <span>{labels.adsEnabled}</span>
          <input
            type="checkbox"
            checked={settings.adsEnabled}
            disabled={adsLocked}
            onChange={(e) => onUpdateSetting('adsEnabled', e.target.checked)}
          />
        </label>
        <label className="setting-row">
          <span>{labels.personalizedAds}</span>
          <input
            type="checkbox"
            checked={settings.adPersonalization}
            onChange={(e) => {
              void onUpdateAdPersonalization(e.target.checked);
            }}
          />
        </label>
        <div className="small options-inline-note">
          {labels.personalizedAdsStatus}: {adConsentStatusLabel}
        </div>
        <div className="small options-inline-note">{labels.adsCmpNotice}</div>
        <div className="settings-actions-inline options-single-action">
          <button className="settings-action" disabled={isWorking} onClick={onWithdrawAdConsent}>
            {labels.withdrawAdConsent}
          </button>
        </div>
        <div className="settings-actions-inline options-single-action">
          <button
            className="settings-action"
            disabled={isWorking || !adPrivacyOptionsAvailable}
            onClick={() => void onOpenAdPrivacyOptions()}
          >
            {labels.adPrivacyOptions}
          </button>
        </div>
        <div className="card">
          <div className="options-card-intro small">{labels.billingDescription}</div>
          <div className="options-meta-block">
            <div className="small">{billingConfigured ? labels.billingConfigured : labels.billingNotConfigured}</div>
            <div className="small">
              {labels.billingTier}: {tierLabel}
            </div>
          </div>
          <div className="card options-pro-benefits">
            <div className="row-title">{labels.billingProBenefitsTitle}</div>
            <div className="small">{labels.billingProBenefitsBody}</div>
          </div>
          {billingStatusText && (
            <div className="small options-status-text">
              {labels.billingStatus}: {billingStatusText}
            </div>
          )}
          <div className="settings-actions-inline options-action-grid options-billing-actions">
            <button className="settings-action" disabled={isWorking || !billingConfigured} onClick={() => void handleCheckBilling()}>
              {labels.billingCheck}
            </button>
            <button className="settings-action" disabled={isWorking || !billingConfigured} onClick={() => void handleRestoreBilling()}>
              {labels.billingRestore}
            </button>
            <button className="settings-action" disabled={isWorking || !canManageBilling} onClick={() => void handleManageBilling()}>
              {labels.billingManage}
            </button>
          </div>
          <div className="small options-billing-disclosure">{labels.billingDisclosure}</div>
          <div className="settings-actions-inline options-single-action">
            <button className="settings-action" disabled={isWorking} onClick={() => void handleRunBillingDiagnostics()}>
              {labels.billingDiagnosticsRun}
            </button>
          </div>
          {!canManageBilling && <div className="small options-status-text options-inline-note">{labels.billingManageUnavailable}</div>}
          {billingDiagnostics && (
            <div className="card options-billing-diagnostics">
              <div className="row-title">{labels.billingDiagnosticsTitle}</div>
              <div className="small">
                {labels.billingDiagnosticsLastCheck}: {new Date(billingDiagnostics.checkedAtMs).toLocaleString()}
              </div>
              <div className="row-title options-subheading">{labels.billingDiagnosticsChecks}</div>
              <div className="stack">
                {billingDiagnostics.checks.map((check) => (
                  <div className="small" key={check.key}>
                    {check.ok ? labels.billingDiagnosticsCheckOk : labels.billingDiagnosticsCheckNeedsAction}: {check.detail}
                  </div>
                ))}
              </div>
              <div className="row-title options-subheading">{labels.billingDiagnosticsDetails}</div>
              <div className="small">
                {labels.billingDiagnosticsAppUserId}: {billingDiagnostics.appUserId ?? labels.billingDiagnosticsNotAvailable}
              </div>
              <div className="small">
                {labels.billingDiagnosticsOffering}: {billingDiagnostics.currentOfferingId ?? labels.billingDiagnosticsNotAvailable}
              </div>
              <div className="small">
                {labels.billingDiagnosticsPackages}: {billingDiagnostics.availablePackageCount}
              </div>
              <div className="small">
                {labels.billingDiagnosticsEntitlements}:{' '}
                {billingDiagnostics.activeEntitlements.length > 0
                  ? billingDiagnostics.activeEntitlements.join(', ')
                  : labels.billingDiagnosticsNotAvailable}
              </div>
              <div className="small">
                {labels.billingDiagnosticsSubscriptions}:{' '}
                {billingDiagnostics.activeSubscriptions.length > 0
                  ? billingDiagnostics.activeSubscriptions.join(', ')
                  : labels.billingDiagnosticsNotAvailable}
              </div>
              <div className="small">
                {labels.billingDiagnosticsManagementUrl}: {billingDiagnostics.managementUrl ?? labels.billingDiagnosticsNotAvailable}
              </div>
              <div className="row-title options-subheading">{labels.billingDiagnosticsRecentEvents}</div>
              {billingDiagnostics.recentEvents.length === 0 ? (
                <div className="small">{labels.billingDiagnosticsNoEvents}</div>
              ) : (
                <div className="stack diagnostics-event-list">
                  {billingDiagnostics.recentEvents.map((event) => (
                    <div className="small diagnostics-event-row" key={event.id}>
                      <div className="diagnostics-event-meta">
                        <span>{new Date(event.tsMs).toLocaleString()}</span>
                        <span>{event.category}</span>
                        <span>{event.status}</span>
                      </div>
                      <div>{event.detail}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="settings-actions-inline options-single-action">
                <button className="settings-action" disabled={isWorking} onClick={onClearBillingEvents}>
                  {labels.billingDiagnosticsClearEvents}
                </button>
              </div>
              {billingDiagnostics.error && <div className="small sync-status sync-status-error">{billingDiagnostics.error}</div>}
            </div>
          )}
          <div className="row-title options-subheading">{labels.billingPlans}</div>
          {monetizationPlans.length === 0 ? (
            <div className="small">{labels.billingNoPlans}</div>
          ) : (
            <div className="stack">
              {monetizationPlans.map((plan) => (
                <div className="billing-plan-row" key={plan.productId}>
                  <div className="billing-plan-text">
                    <div className="row-title">
                      {plan.kind === 'ad_free' ? labels.billingAdFreeTag : labels.billingProTag}: {plan.title}
                    </div>
                    <div className="small">{plan.priceLabel}</div>
                  </div>
                  {billingTier === 'pro' || (billingTier === 'ad_free' && plan.kind === 'ad_free') ? (
                    <span className="small">{labels.billingCurrent}</span>
                  ) : (
                    <button
                      className="settings-action billing-plan-buy"
                      disabled={isWorking || !billingConfigured}
                      onClick={() => void handleBuyPlan(plan.productId)}
                    >
                      {labels.billingBuy}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <h2>{labels.backup}</h2>
      <div className="stack">
        <div className="card">
          <div className="options-card-intro small">{labels.backupDescription}</div>
          {isWebApp && (
            <div className="options-meta-block">
              <div className="row-title">{labels.localFirstTitle}</div>
              <div className="small">{labels.localFirstBody}</div>
              <div className="small">{labels.localFirstRisk}</div>
            </div>
          )}
          <label className="setting-row">
            <span>{labels.backupPassphrase}</span>
            <input
              type="password"
              className="setting-select"
              value={backupPassphrase}
              onChange={(e) => setBackupPassphrase(e.target.value)}
              autoComplete="new-password"
            />
          </label>
          <div className="settings-actions-inline options-action-grid">
            <button className="settings-action" disabled={isWorking} onClick={() => void handleCreateBackup()}>
              {labels.backupCreate}
            </button>
            <button className="settings-action" disabled={isWorking} onClick={() => restoreInputRef.current?.click()}>
              {labels.backupRestore}
            </button>
          </div>
          <input
            ref={restoreInputRef}
            className="hidden-input"
            type="file"
            accept="application/json,.json"
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              if (!file) return;
              void handleRestoreFile(file);
            }}
          />
          {syncActivity && (
            <div className="sync-status sync-status-progress">
              <span className="sync-spinner" aria-hidden="true" />
              <span>
                {labels.syncInProgress}: {syncActivity}
              </span>
            </div>
          )}
          {backupStatus && (
            <div className={`small sync-status sync-status-${backupStatusTone}`}>
              {labels.backupStatusPrefix}: {backupStatus}
            </div>
          )}
        </div>
      </div>

      {!isWebApp && (
        <>
          <h2>{labels.drive}</h2>
          <div className="stack">
            <div className="card">
              <div className="options-card-intro small">{labels.driveDescription}</div>
              <div className="options-meta-block">
                <div className="small">{driveConfigured ? labels.driveConfigured : labels.driveNotConfigured}</div>
                <div className="small">{driveConnected ? labels.driveConnected : labels.driveNotConnected}</div>
              </div>
              <div className="settings-actions-inline options-drive-actions">
                {!driveConnected ? (
                  <button className="settings-action" disabled={isWorking || !driveConfigured} onClick={() => void handleDriveConnect()}>
                    {labels.driveConnect}
                  </button>
                ) : (
                  <button className="settings-action" disabled={isWorking} onClick={onDisconnectDrive}>
                    {labels.driveDisconnect}
                  </button>
                )}
                <button className="settings-action" disabled={isWorking || !driveConfigured} onClick={() => void handleDriveUpload()}>
                  {labels.driveUpload}
                </button>
                <button className="settings-action" disabled={isWorking || !driveConfigured} onClick={() => void handleDriveRestore()}>
                  {labels.driveRestore}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <h2>{labels.help}</h2>
      <div className="stack">
        <div className="card">
          <div className="row-title">{labels.replayTitle}</div>
          <div className="small">{labels.replayDescription}</div>
          <div className="settings-actions-inline options-single-action">
            <button className="settings-action" onClick={onReplayOnboarding}>
              {labels.replayButton}
            </button>
          </div>
        </div>
      </div>

      <h2>{labels.privacyData}</h2>
      <div className="stack">
        <div className="card">
          <div className="options-card-intro small">{labels.privacyDescription}</div>
          <div className="small options-legal-note">{labels.privacyRightsSla}</div>
          <div className="small options-legal-note">{labels.childrenNotice}</div>
          <div className="small options-legal-note">{labels.medicalDisclaimer}</div>
          <div className="settings-actions-inline options-action-grid">
            <button className="settings-action" disabled={isWorking} onClick={() => void handlePrivacyPolicy()}>
              {labels.privacyPolicy}
            </button>
            <button className="settings-action" disabled={isWorking} onClick={() => void handleTerms()}>
              {labels.terms}
            </button>
          </div>
          <div className="settings-actions-inline options-action-grid">
            <button className="settings-action" disabled={isWorking} onClick={() => void handleContactSupport()}>
              {labels.contactSupport}
            </button>
            <button className="settings-action" disabled={isWorking} onClick={() => void handleExportData()}>
              {labels.exportData}
            </button>
          </div>
          <div className="settings-actions-inline options-single-action">
            <button className="settings-action danger-action" disabled={isWorking} onClick={() => void handleDeleteAllData()}>
              {labels.deleteData}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
